import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { RiArrowRightLine, RiSurveyLine, RiTaskLine } from '@remixicon/react'
import type { SurveyListItem, Task } from 'shared'
import { formatDate } from '../../lib/dates'
import { cx, focusRing, neutralTile } from '../../lib/utils'

interface WhatIsYoursProps {
  workspaceId: string
  surveys: SurveyListItem[]
  tasks: Task[]
}

/**
 * What this company still owes, at the top of their page.
 *
 * The timeline below answers "what happened", which is not the question
 * someone opens this page with — they want to know whether anything is
 * waiting on them. It used to be the only answer available, and a
 * chronological log buries a survey published two weeks ago under everything
 * that happened since. So the open work is pulled out and put first, and the
 * section disappears entirely when there is none: an empty call to action is
 * worse than no call to action.
 */
export default function WhatIsYours({ workspaceId, surveys, tasks }: WhatIsYoursProps) {
  const { t, i18n } = useTranslation()

  // A survey is owed until it has been submitted; a task until it is done.
  const openSurveys = surveys.filter(
    (survey) =>
      survey.status === 'published' &&
      survey.can_answer !== false &&
      survey.submission_status !== 'submitted',
  )
  const openTasks = tasks.filter(
    (task) => task.status === 'open' || task.status === 'in_progress',
  )

  if (openSurveys.length === 0 && openTasks.length === 0) return null

  return (
    <section aria-labelledby="whats-yours" className="mb-8">
      <h2 id="whats-yours" className="text-display-sm text-gray-950 dark:text-white">
        {t('member.yoursTitle')}
      </h2>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{t('member.yoursSubtitle')}</p>

      <ul className="mt-4 space-y-3">
        {openSurveys.map((survey) => (
          <li key={survey.id}>
            <Row
              to={`/w/${workspaceId}/surveys/${survey.id}`}
              icon={<RiSurveyLine className="size-5" aria-hidden="true" />}
              accent={neutralTile}
              title={survey.title}
              /* Not "started": the collective submission is created when the
                 survey is published, so `in_progress` says it is open for
                 answers, never that this team has touched it. Claiming
                 otherwise told people they had begun something they had not. */
              meta={t('surveys.questionCount', { count: survey.question_count })}
              due={survey.due_at ? formatDate(survey.due_at, i18n.language) : null}
              dueLabel={t('member.dueBy')}
            />
          </li>
        ))}

        {openTasks.map((task) => (
          <li key={task.id}>
            <Row
              to={`/w/${workspaceId}/tasks`}
              icon={<RiTaskLine className="size-5" aria-hidden="true" />}
              accent={neutralTile}
              title={task.title}
              meta={task.description ?? t(`tasks.status_${task.status}`)}
              due={task.due_at ? formatDate(task.due_at, i18n.language) : null}
              dueLabel={t('member.dueBy')}
            />
          </li>
        ))}
      </ul>
    </section>
  )
}

interface RowProps {
  to: string
  icon: React.ReactNode
  accent: string
  title: string
  meta: string
  due: string | null
  dueLabel: string
}

function Row({ to, icon, accent, title, meta, due, dueLabel }: RowProps) {
  return (
    <Link
      to={to}
      className={cx(
        'group flex items-center gap-4 rounded-xl bg-white p-4 ring-1 ring-gray-200 transition duration-150',
        'hover:-translate-y-0.5 hover:shadow-lg hover:shadow-gray-900/5 hover:ring-gray-300',
        'dark:bg-gray-950 dark:ring-gray-800 dark:hover:ring-gray-700',
        focusRing,
      )}
    >
      <span
        className={cx(
          'flex size-10 shrink-0 items-center justify-center rounded-lg ring-1',
          accent,
        )}
      >
        {icon}
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate font-semibold text-gray-950 dark:text-white">{title}</span>
        <span className="mt-0.5 block truncate text-sm text-gray-500 dark:text-gray-400">
          {meta}
        </span>
      </span>

      {due && (
        <span className="hidden shrink-0 text-xs text-gray-500 sm:block dark:text-gray-400">
          {dueLabel} {due}
        </span>
      )}

      <RiArrowRightLine
        className="size-4 shrink-0 text-gray-400 transition group-hover:translate-x-0.5 group-hover:text-primary-500"
        aria-hidden="true"
      />
    </Link>
  )
}
